import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useLocation } from "react-router";
import dayjs from "dayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis
} from "recharts";

import Header from "components/Header";
import Select from "components/Select";
import { GRAPH_TYPES, RATE_TREND_COLORS, RATE_TREND_EQUIPMENT_TYPES } from "constants/rate-trend";
import { formatNumber } from "helpers/formatNumber";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { IOption } from "models/Form";
import { IChartData, IRateTrendResponse, LaneTrendType } from "models/RateTrend";
import styles from "./style.module.scss";

const getEquipmentType = (type: string | number) =>
  RATE_TREND_EQUIPMENT_TYPES.find((eq) => eq.value === String(type))?.subLabel;

const CustomAxisTick = (props: any) => {
  const { isDesktop } = useDeviceType();

  return (
    <g transform={`translate(${props.x},${props.y})`}>
      <text x={0} y={0} dy={16} fontSize={isDesktop ? 13 : 9} textAnchor="middle" fill="#586576">
        {dayjs(props.payload.value).format("MM-DD")}
      </text>
    </g>
  );
};

const CustomTooltip = (props: any) => {
  const { active, payload } = props;
  if (active && payload?.length) {
    return (
      <ul className="p-2 desktop:p-4 bg-background rounded-base">
        {payload.map((item: any) => (
          <li key={item.dataKey} className="flex items-center gap-1 desktop:gap-3">
            <div
              className="size-1.5 desktop:size-3 rounded-full desktop:rounded-base"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs desktop:text-sm">
              <span className="text-text">{getEquipmentType(item.dataKey)}: </span>
              <b>{formatNumber(item.value / 100, 2)}</b>
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return null;
};

const CustomLegend = (props: any) => (
  <ul className="flex items-center justify-between mt-4">
    {props.payload.map((item: any) => (
      <li key={item.value} className="flex items-center gap-1">
        <div className="size-3" style={{ backgroundColor: item.color }} />
        <span className="text-xs desktop:text-base text-text">{getEquipmentType(item.value)}</span>
      </li>
    ))}
  </ul>
);

export default function RateTrendViewGraph() {
  const { state } = useLocation();
  const [chartData, setChartData] = useState<IChartData[]>([]);

  const { control, reset, handleSubmit } = useForm<{
    graphTypes: IOption;
  }>();
  const laneTrendType = useWatch({ control: control, name: "graphTypes" });

  const processLaneTrendData = async (
    data: IRateTrendResponse[],
    type: LaneTrendType
  ): Promise<IChartData[]> => {
    const uniqueTrailerTypes = Array.from(new Set(data.map((item) => item.trailerType.toString())));

    const result: Record<string, Record<string, { total: number; count: number }>> = {};

    for (const item of data) {
      if (!item.created) break;
      const date = item.created.split("T")[0];
      const trailerType = item.trailerType.toString();

      if (!result[date]) {
        result[date] = {};
      }

      if (!result[date][trailerType]) {
        result[date][trailerType] = { total: 0, count: 0 };
      }

      let amount = 0;
      if (type === LaneTrendType.METRIC) {
        amount = item.amount;
      } else if (type === LaneTrendType.RATE_PER_MILE) {
        amount = Number(state?.rateDetailsData?.average_rate) / Number(item.distance) / 100;
      } else if (type === LaneTrendType.FUEL_COSTS) {
        amount = Number(state?.rateDetailsData?.linehaul_cpg / 6) * Number(item.distance);
      } else if (type === LaneTrendType.AVG_LINE) {
        amount = Number(state?.rateDetailsData?.average_rate) / 100;
      }

      result[date][trailerType].total += amount;
      result[date][trailerType].count += 1;
    }

    return Object.keys(result).map((date) => {
      const dataForDate: IChartData = { name: date };

      uniqueTrailerTypes.forEach((type) => {
        const trailerData = result[date][type];
        dataForDate[type] = trailerData ? trailerData.total / trailerData.count : 0;
      });

      return dataForDate;
    });
  };

  const sortedChartData = useMemo(() => {
    if (chartData.length) {
      return chartData.sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime());
    } else {
      return [];
    }
  }, [chartData]);

  const onSubmit = () => {};

  useEffect(() => {
    const fetchData = async () => {
      if (state && laneTrendType) {
        const data = await processLaneTrendData(
          state?.laneTrendData,
          laneTrendType.value as LaneTrendType
        );
        setChartData(data);
      }
    };

    fetchData();
  }, [state, laneTrendType]);

  useEffect(() => {
    reset({
      graphTypes: GRAPH_TYPES[0]
    });
  }, []);

  return (
    <MainLayout
      desktopTitle="Rate Trend"
      className={styles.trendGraphContainer}
      customHeader={
        <Header label="View Rate Trend Graph" className="text-lg font-medium leading-6" />
      }>
      <div className={styles.trendGraphWrapper}>
        <form className={styles.trendGraphHeader} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.trendGraphAverage}>
            <span className="text-xs text-text desktop:text-base">Average</span>
            <span className="text-sm desktop:text-lg">
              {formatNumber(state?.rateDetailsData?.average_rate / 100, 2)}
            </span>
          </div>
          <Controller
            name="graphTypes"
            control={control}
            render={({ field: { value = GRAPH_TYPES[0], onChange } }) => (
              <Select
                options={GRAPH_TYPES}
                value={value}
                onChange={onChange}
                className="!w-60 mobile:w-2/3"
                btnClass="mobile:!py-2 mobile:h-11 mobile:!px-4 mobile:!text-sm"
              />
            )}
          />
        </form>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            className="w-full"
            data={sortedChartData}
            margin={{
              left: 16,
              right: 16
            }}>
            {Object.keys(sortedChartData[0] || {})
              .filter((key) => key !== "name")
              .map((key, index) => (
                <Line
                  key={key}
                  dataKey={key}
                  stroke={RATE_TREND_COLORS[index].color}
                  strokeWidth={2}
                  dot={{ strokeWidth: 1, fill: RATE_TREND_COLORS[index].color }}
                />
              ))}
            <Tooltip content={<CustomTooltip />} />
            <XAxis
              dataKey="name"
              strokeDasharray={4}
              tickLine={false}
              tick={<CustomAxisTick />}
              interval={0}
            />
            <CartesianGrid strokeDasharray={4} stroke="#374048" vertical={false} />
            <Legend content={<CustomLegend />} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </MainLayout>
  );
}
